import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useStage } from "./Stage";
import { StageContext } from "./hooks";
import { useRouteSeo } from "./seo";
import Wordmark from "./Wordmark";
import { EMAIL } from "@/lib/contact";
import Channels from "./Channels";
import {
  LANGS, LANG_LABEL, LANG_NAME, hasTranslation, stripLang, useLang, useLangPath, useT, withLang,
} from "@/i18n";

/* Paleta e una singură — Graphite — și trăiește în :root din site.css. Comuta-
   torul de patru direcții era unealtă de prototip. Motorul 3D primește doar
   indexul, ca să-și potrivească materialele de scenă. */
const SKIN = 2;

/* Bara trimite la pagini și la secțiuni, nu la proiecte individuale. Ancorele
   duc pe landing: HashScrollHandler navighează acasă și derulează, deci merg și
   de pe o pagină de caz. */
const NAV = [
  { to: "/", key: "index" },
  { to: "/services", key: "services" },
  { to: "/work", key: "work" },
  { to: "/blog", key: "blog" },
  { to: "/faq", key: "faq" },
  { to: "/contact", key: "contact" },
] as const;

/** Bara devine opacă imediat ce pagina s-a mișcat.
 *
 *  Starea asta venea din motorul 3D: `engine.js` chema `onScrolled(scrollY>60)`
 *  din bucla lui de randare. Deci pe orice pagină fără scenă — pagina
 *  principală, serviciile, „Despre", răspunsurile, contactul, legalele —
 *  motorul nu se încărca niciodată, callbackul nu se chema niciodată, iar bara
 *  rămânea transparentă la orice poziție de scroll: textul paginii trecea pe
 *  sub logotip și pe sub navigație, și nu se mai citea niciunul. Pe telefon,
 *  unde bara acoperă 81px dintr-un ecran de 844, se întâmpla la fiecare
 *  degetul dat în jos.
 *
 *  Acum ascultă scroll-ul direct. Nu are ce căuta în motorul de scenă: e o
 *  proprietate a ferestrei, nu a modelului. */
function useScrolledPastTop(px = 24) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const read = () => setPast(window.scrollY > px);
    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, [px]);
  return past;
}

export default function Shell() {
  const { pathname } = useLocation();
  const { canvasRef, phase, setScene } = useStage(SKIN);
  const scrolled = useScrolledPastTop();
  useRouteSeo();
  const t = useT();
  const lang = useLang();
  const lp = useLangPath();

  /* Adresa fără prefix, ca potrivirile să funcționeze la fel în toate limbile:
     „/ro/work/erp" și „/work/erp" trebuie să însemne același caz. */
  const bare = stripLang(pathname);
  const bars = useMemo(() => t.cases.find((c) => `/work/${c.slug}` === bare)?.phases, [t, bare]);
  const ctx = useMemo(() => ({ setScene }), [setScene]);

  /* Meniul de telefon. Sub 900px bara ascundea navigația și adresa și nu punea
     nimic în loc: singurul drum spre Services, Work, FAQ sau Contact era să
     derulezi landing-ul până în subsol, iar de pe orice altă pagină nu exista
     drum înapoi în afară de butonul browserului. */
  const [menu, setMenu] = useState(false);

  /* Se închide singur la schimbarea rutei: altfel un link apăsat în meniu ar
     duce la pagina nouă cu panoul încă deschis peste ea. */
  useEffect(() => setMenu(false), [pathname]);

  /* Cât e deschis: Escape îl închide, iar pagina de sub el nu se mai derulează
     — un panou care acoperă ecranul peste conținut care fuge pe sub el e o
     capcană pe touch. */
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  return (
    <StageContext.Provider value={ctx}>
      <canvas id="gl" ref={canvasRef} aria-hidden="true" />
      <div id="glfallback" aria-hidden="true" />
      <div className="grain" aria-hidden="true" />

      <header className={"bar" + (scrolled ? " small" : "")} id="bar">
        <Link className="wmlink" to={lp("/")} aria-label="coresmith.dev">
          <Wordmark />
        </Link>
        <nav className="barnav" id="barnav">
          {NAV.map((n) => (
            <Link key={n.to} to={lp(n.to)} className={bare === n.to ? "on" : undefined}>
              {t.nav[n.key]}
            </Link>
          ))}
        </nav>
        {/* Comutatorul de limbă duce la aceeași pagină în limba cealaltă. Pe
            paginile legale, care există doar în engleză, duce la pagina
            principală a limbii — mai bine decât un link care nu are unde să
            meargă sau un comutator care dispare fără explicație. */}
        <div className="barend">
          {/* Adresa în bară, ca la agenții: un drum de contact deschis pe orice
              pagină, fără să treci prin formular. Ascunsă pe ecrane mici, unde ar
              împinge comutatorul de limbă afară. */}
          <a className="baremail" href={`mailto:${EMAIL}`}>{EMAIL}</a>
          {/* Doar mărcile: în bară nu e loc de cuvinte, iar numele aplicației
              ajunge în aria-label și în title. */}
          <span className="barchans"><Channels bare /></span>
          <nav className="langs" aria-label="Language">
          {LANGS.map((l) => (
            <Link
              key={l}
              to={withLang(l, hasTranslation(bare) ? bare : "/")}
              className={l === lang ? "on" : undefined}
              hrefLang={l}
              aria-current={l === lang ? "true" : undefined}
              title={LANG_NAME[l]}
            >
              {LANG_LABEL[l]}
            </Link>
            ))}
          </nav>
          {/* Butonul apare doar sub 900px, exact unde dispare .barnav. */}
          <button
            type="button"
            className={"burger" + (menu ? " on" : "")}
            aria-expanded={menu}
            aria-controls="mobnav"
            aria-label={menu ? t.nav.closeMenu : t.nav.menu}
            onClick={() => setMenu((v) => !v)}
          >
            <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Panoul. Rămâne montat ca tranziția să aibă de unde pleca, dar primește
          hidden când e închis: altfel cele cinci linkuri ar rămâne în ordinea
          de tabulare și în arborele de accesibilitate pe o pagină pe care nu se
          văd. */}
      <div className={"mobnav" + (menu ? " on" : "")} id="mobnav" hidden={!menu}>
        <nav aria-label={t.nav.menu}>
          {NAV.map((n) => (
            <Link key={n.to} to={lp(n.to)} className={bare === n.to ? "on" : undefined}>
              {t.nav[n.key]}
            </Link>
          ))}
        </nav>
        <a className="mobmail" href={`mailto:${EMAIL}`}>{EMAIL}</a>
        <div className="mobchans"><Channels /></div>
      </div>

      {/* Bara de faze e position:fixed pe jos, deci la capătul paginii acoperea
          ultimii ~45px, adică o parte din footer. Clasa spune paginii să lase loc
          exact cât ocupă bara. */}
      <main className={bars ? "hasbar" : undefined}>
        <Outlet />
      </main>

      <div className={"phasebar" + (bars ? " on" : "")} id="phasebar" aria-hidden="true">
        <div className="phasebar-in">
          <ol id="phaseList">
            {bars?.steps.map((s, i) => (
              <li key={s} className={i === phase ? "on now" : i < phase ? "on" : undefined}>
                <span className="pip" />
                <b>{s}</b>
              </li>
            ))}
          </ol>
          <span id="phaseHint">{bars?.hint}</span>
        </div>
      </div>
    </StageContext.Provider>
  );
}
